package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.foundation.Canvas
import androidx.compose.ui.graphics.drawscope.clipRect
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.TextUnitType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.em
import androidx.compose.ui.unit.sp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.pages.DashboardViewModel
import com.example.mystybag.appCore.model.pages.DashboardPeriod
import com.example.mystybag.appCore.model.pages.DashboardUIState
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.rememberUseNativeStyleWebLayout
import com.example.mystybag.sharedmodels.project.logic.enums.ReservationStatus
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.common.ui.components.custom.DatePicker
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource
import com.example.mystybag.core.ui.theme.LocalTheme.p10
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p28
import com.example.mystybag.core.ui.theme.LocalTheme.p2
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p40
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p9
import com.example.mystybag.core.views.AppTheme
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.min
import kotlin.math.sin

/** width:height — web single row (original card height). */
private const val OVERVIEW_MERCHANT_KPI_ASPECT_WEB = 255f / 160f

/** width:height — native 2×2 grid (half height vs web row). */
private const val OVERVIEW_MERCHANT_KPI_ASPECT_NATIVE = 255f / 80f

@Composable
private fun getCategoryLabel(categoryKey: String): String = when (categoryKey) {
    "review_rating_taste" -> stringResource(SR.strings.review_rating_taste)
    "review_rating_quality" -> stringResource(SR.strings.review_rating_quality)
    "review_rating_freshness" -> stringResource(SR.strings.review_rating_freshness)
    "review_rating_value" -> stringResource(SR.strings.review_rating_value)
    else -> categoryKey
}

@Composable
fun DashboardView(viewModel: DashboardViewModel) {
    val colors = AppTheme.current.colors

    UIView(
        modifier = Modifier
            .fillMaxSize()
            .background(colors.background),
        viewModel = viewModel,
        showTopBar = false,
        removeVerticalPadding = true,
        applyBottomInsetSpacer = false
    ) {
        DashboardMainScreenContent(viewModel = viewModel)
    }
}

/** Shared body for [DashboardView] (native / web portrait) and [DashboardWebView] (web landscape). */
@Composable
internal fun DashboardMainScreenContent(viewModel: DashboardViewModel) {
    val uiState by viewModel.uiState.subscribeAsState()
    val colors = AppTheme.current.colors
    val useNativeStyleWebLayout = rememberUseNativeStyleWebLayout()
    Box(modifier = Modifier.fillMaxSize()) {
        when {
            uiState.isLoading -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator()
                }
            }

            uiState.locations.isEmpty() -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(p20),
                    contentAlignment = Alignment.Center
                ) {
                    Texts.BodyMedium(text = sharedString(SR.strings.no_locations_found))
                }
            }

            else -> {
                LazyColumn(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(start = p16, end = p16, top = p16)
                        .scrollEdgeFade(
                            showTop = false,
                            edgeSize = 14.dp,
                            overlayColor = AppTheme.current.colors.background.copy(alpha = 0.65f)
                        ),
                    verticalArrangement = Arrangement.spacedBy(0.dp)
                ) {
                    if (useNativeStyleWebLayout) {
                        item {
                            Texts.DisplaySmall(
                                text = sharedString(SR.strings.overview),
                                modifier = Modifier.fillMaxWidth(),
                                color = colors.onSurface
                            )
                            Spacer(modifier = Modifier.height(p12))
                        }
                    }
                    item {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(p12)
                        ) {
                            OverviewFilterPill(
                                text = if (uiState.selectedLocationId == null) sharedString(SR.strings.all_locations)
                                else uiState.locations.find { it.id == uiState.selectedLocationId }?.title
                                    ?: sharedString(SR.strings.all_locations),
                                expanded = { viewModel.selectLocation(it) },
                                locations = uiState.locations,
                                selectedLocationId = uiState.selectedLocationId
                            )
                            OverviewFilterPillPeriod(
                                selectedPeriod = uiState.selectedPeriod,
                                onPeriodSelected = { viewModel.selectPeriod(it) }
                            )
                        }
                        if (uiState.selectedPeriod == DashboardPeriod.PERIOD) {
                            Spacer(modifier = Modifier.height(p12))
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(p8)
                            ) {
                                DatePicker(
                                    modifier = Modifier.weight(1f),
                                    value = uiState.customPeriodFrom,
                                    onValueChange = viewModel::onCustomPeriodFromChanged,
                                    label = sharedString(SR.strings.start_date),
                                    maxDate = uiState.customPeriodTo
                                )
                                DatePicker(
                                    modifier = Modifier.weight(1f),
                                    value = uiState.customPeriodTo,
                                    onValueChange = viewModel::onCustomPeriodToChanged,
                                    label = sharedString(SR.strings.end_date),
                                    minDate = uiState.customPeriodFrom
                                )
                            }
                        }
                        Spacer(modifier = Modifier.height(p24))
                    }

                    item {
                        OverviewMerchantKpiCards(uiState = uiState)
                        Spacer(modifier = Modifier.height(p8))
                    }

                    item {
                        AvgRatingCard(
                            rating = uiState.averageRating,
                            reviewCount = uiState.reviewCount
                        )
                        Spacer(modifier = Modifier.height(p40))
                    }

                    item {
                        Texts.LabelSmall(
                            text = sharedString(SR.strings.my_reservations),
                            color = AppTheme.current.colors.onSurfaceVariant
                        )
                        Spacer(modifier = Modifier.height(p12))
                        HorizontalDivider(
                            modifier = Modifier.fillMaxWidth(),
                            thickness = 1.dp,
                            color = AppTheme.current.colors.outlineVariant
                        )
                        Spacer(modifier = Modifier.height(p12))
                    }

                    if (uiState.reservations.isEmpty()) {
                        item {
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(top = p20, bottom = p40),
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.spacedBy(p12)
                            ) {
                                Image(
                                    painter = painterResource(SRC.images.add_first_bag_icon),
                                    modifier = Modifier
                                        .height(132.dp)
                                        .width(99.dp)
                                )
                                Texts.TitleMedium(
                                    text = sharedString(SR.strings.no_reservations),
                                    color = AppTheme.current.colors.onSurface
                                )
                            }
                        }
                    } else {
                        items(
                            items = uiState.reservations,
                            key = { it.id ?: 0L }
                        ) { reservation ->
                            DashboardReservationCard(
                                reservation = reservation,
                                modifier = Modifier.fillMaxWidth()
                            )
                            Spacer(modifier = Modifier.height(p12))
                        }
                    }
                }
            }
        }
    }
}

/** Native: two rows (Приходи / Продажби, then Брой торби / За предаване). Web: single row. */
@Composable
private fun OverviewMerchantKpiCards(uiState: DashboardUIState) {
    val revenueText = if (uiState.canViewRevenue) {
        "${com.example.mystybag.core.expected.format("%.2f", uiState.totalRevenue)} €"
    } else {
        // Staff without revenue permission: mask the value while preserving the "€" suffix for layout parity.
        "**.** €"
    }
    if (PlatformHelper.isWeb) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            OverviewMetricCard(
                modifier = Modifier.weight(1f).aspectRatio(OVERVIEW_MERCHANT_KPI_ASPECT_WEB),
                title = sharedString(SR.strings.revenue),
                value = revenueText,
                valueKind = OverviewMetricValueKind.REVENUE
            )
            OverviewMetricCard(
                modifier = Modifier.weight(1f).aspectRatio(OVERVIEW_MERCHANT_KPI_ASPECT_WEB),
                title = sharedString(SR.strings.sales),
                value = uiState.soldBagsCount.toString(),
                valueKind = OverviewMetricValueKind.DISPLAY_SMALL
            )
            OverviewMetricCard(
                modifier = Modifier.weight(1f).aspectRatio(OVERVIEW_MERCHANT_KPI_ASPECT_WEB),
                title = sharedString(SR.strings.sold_bags_units),
                value = uiState.soldBagUnitsTotal.toString(),
                valueKind = OverviewMetricValueKind.DISPLAY_SMALL
            )
            OverviewMetricCard(
                modifier = Modifier.weight(1f).aspectRatio(OVERVIEW_MERCHANT_KPI_ASPECT_WEB),
                title = sharedString(SR.strings.for_handover),
                value = uiState.toDeliverCount.toString(),
                valueKind = OverviewMetricValueKind.DISPLAY_SMALL
            )
        }
    } else {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                OverviewMetricCard(
                    modifier = Modifier.weight(1f).aspectRatio(OVERVIEW_MERCHANT_KPI_ASPECT_NATIVE),
                    title = sharedString(SR.strings.revenue),
                    value = revenueText,
                    valueKind = OverviewMetricValueKind.REVENUE
                )
                OverviewMetricCard(
                    modifier = Modifier.weight(1f).aspectRatio(OVERVIEW_MERCHANT_KPI_ASPECT_NATIVE),
                    title = sharedString(SR.strings.sales),
                    value = uiState.soldBagsCount.toString(),
                    valueKind = OverviewMetricValueKind.DISPLAY_SMALL
                )
            }
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                OverviewMetricCard(
                    modifier = Modifier.weight(1f).aspectRatio(OVERVIEW_MERCHANT_KPI_ASPECT_NATIVE),
                    title = sharedString(SR.strings.sold_bags_units),
                    value = uiState.soldBagUnitsTotal.toString(),
                    valueKind = OverviewMetricValueKind.DISPLAY_SMALL
                )
                OverviewMetricCard(
                    modifier = Modifier.weight(1f).aspectRatio(OVERVIEW_MERCHANT_KPI_ASPECT_NATIVE),
                    title = sharedString(SR.strings.for_handover),
                    value = uiState.toDeliverCount.toString(),
                    valueKind = OverviewMetricValueKind.DISPLAY_SMALL
                )
            }
        }
    }
}

@Composable
private fun DashboardOverviewComboArrow() {
    Image(
        painter = painterResource(SRC.images.combobox_arrow_down_green),
        contentScale = ContentScale.Fit,
        modifier = Modifier.size(p16),
        colorFilter = ColorFilter.tint(AppTheme.current.colors.primary)
    )
}

/** Тип на стойността в KPI картата: Оборот → TitleMedium; Продажби / За предаване → DisplaySmall */
private enum class OverviewMetricValueKind {
    REVENUE,
    DISPLAY_SMALL
}

/** Минимален шрифт (sp) за KPI заглавия – един ред с autosize (ляво в реда). */
private const val OVERVIEW_METRIC_TITLE_MIN_SP = 10f

/** Минимален шрифт за KPI стойности – един ред с autosize (дясно в реда). */
private const val OVERVIEW_METRIC_VALUE_MIN_SP = 12f

/** Мащабира fontSize и lineHeight заедно, за да не остане голям line box при малък шрифт (реже се от родителя). */
private fun TextStyle.withScaledFontAndLine(fontSizeSp: Float, color: Color): TextStyle {
    val denom = fontSize.value.takeIf { it > 0f } ?: return copy(fontSize = fontSizeSp.sp, color = color)
    val ratio = fontSizeSp / denom
    val newLineHeight = when {
        lineHeight == TextUnit.Unspecified -> TextUnit.Unspecified
        lineHeight.type == TextUnitType.Sp -> (lineHeight.value * ratio).sp
        lineHeight.type == TextUnitType.Em -> (lineHeight.value * ratio).em
        else -> lineHeight
    }
    return copy(fontSize = fontSizeSp.sp, lineHeight = newLineHeight, color = color)
}

@Composable
private fun AutoResizeSingleLineText(
    text: String,
    baseStyle: TextStyle,
    color: Color,
    minFontSizeSp: Float,
    modifier: Modifier = Modifier,
    textAlign: TextAlign = TextAlign.Start,
    /**
     * Subtracted from available width when checking fit. Web/Skiko can paint trailing glyphs
     * (e.g. €) slightly wider than [TextMeasurer]; short strings like "0.00 €" keep max font and then clip.
     */
    horizontalFitSlackDp: Dp = 0.dp,
) {
    BoxWithConstraints(modifier.fillMaxWidth().fillMaxHeight()) {
        val density = LocalDensity.current
        val c = constraints
        val maxPx = (if (c.hasBoundedWidth) c.maxWidth else with(density) { maxWidth.toPx() }.toInt())
            .coerceAtLeast(1)
        val slackPx = with(density) { horizontalFitSlackDp.toPx() }.toInt()
            .coerceIn(0, (maxPx - 1).coerceAtLeast(0))
        val widthBudgetPx = (maxPx - slackPx).coerceAtLeast(1)
        val maxHeightPx = if (c.hasBoundedHeight) c.maxHeight.coerceAtLeast(1) else Int.MAX_VALUE
        val textMeasurer = rememberTextMeasurer()
        val maxFs = baseStyle.fontSize.value
        val fontSizeSp = remember(text, widthBudgetPx, maxHeightPx, maxFs, minFontSizeSp, baseStyle, color) {
            if (text.isEmpty()) return@remember maxFs.sp
            fun fitsAt(sizeSp: Float): Boolean {
                val style = baseStyle.withScaledFontAndLine(sizeSp, color)
                val layout = textMeasurer.measure(
                    text = AnnotatedString(text),
                    style = style,
                    maxLines = 1,
                    softWrap = false,
                    overflow = TextOverflow.Clip
                )
                return layout.size.width <= widthBudgetPx && layout.size.height <= maxHeightPx
            }
            if (fitsAt(maxFs)) return@remember maxFs.sp
            var best = minFontSizeSp
            var lo = minFontSizeSp
            var hi = maxFs
            repeat(30) {
                val mid = (lo + hi) / 2f
                if (fitsAt(mid)) {
                    best = mid
                    lo = mid
                } else {
                    hi = mid
                }
            }
            best.sp
        }
        Text(
            text = text,
            modifier = Modifier.fillMaxWidth(),
            maxLines = 1,
            softWrap = false,
            overflow = TextOverflow.Clip,
            style = baseStyle.withScaledFontAndLine(fontSizeSp.value, color),
            textAlign = textAlign
        )
    }
}


@Composable
private fun DashboardReservationCard(
    reservation: com.example.mystybag.sharedmodels.project.responseModels.merchant.ReservationItem,
    modifier: Modifier = Modifier
) {
    val status = ReservationStatus.fromInt(reservation.status ?: ReservationStatus.RESERVED.code)
    val isCollected = status == ReservationStatus.DELIVERED_TO_CLIENT || status == ReservationStatus.PAID_TO_MERCHANT
    val isDeclined = status == ReservationStatus.CANCELED
    val isPaid = status == ReservationStatus.PAID
    val statusLabel = when {
        isCollected -> sharedString(SR.strings.collected)
        isDeclined -> sharedString(SR.strings.declined)
        isPaid -> sharedString(SR.strings.paid)
        else -> sharedString(SR.strings.reserved)
    }
    val statusBg = when {
        isCollected -> Color(0xFFDEF9DA)
        isPaid -> Color(0xFFE3F2FD)
        else -> Color(0xFFE4D5CE)
    }
    val statusColor = when {
        isCollected -> AppTheme.current.colors.onPrimaryContainer
        isPaid -> Color(0xFF1565C0)
        else -> Color(0xFF4B2F2B)
    }
    val userName = listOf(
        reservation.userFirstName?.trim().orEmpty(),
        reservation.userLastName?.trim().orEmpty()
    ).filter { it.isNotBlank() }.joinToString(" ").ifBlank { "User" }

    val bagName = reservation.bagName?.takeIf { it.isNotBlank() } ?: sharedString(SR.strings.surprise_bag)
    val bagsCount = (reservation.bagsCount ?: 1).coerceAtLeast(1)
    val currency = reservation.currency?.takeIf { it.isNotBlank() } ?: "EUR"
    val amountText = if (currency.equals("EUR", ignoreCase = true)) {
        "${com.example.mystybag.core.expected.format("%.2f", reservation.amount ?: 0.0)} €"
    } else {
        "${com.example.mystybag.core.expected.format("%.2f", reservation.amount ?: 0.0)} $currency"
    }

    Card(
        modifier = modifier
            .border(1.dp, AppTheme.current.colors.outlineVariant, RoundedCornerShape(p28)),
        shape = RoundedCornerShape(p28),
        colors = CardDefaults.cardColors(containerColor = AppTheme.current.colors.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(p16),
            verticalArrangement = Arrangement.spacedBy(p8)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Top
            ) {
                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(p4)
                ) {
                    Texts.BodySmall(
                        text = "#${reservation.id ?: 0L}",
                        color = AppTheme.current.colors.onSurfaceVariant
                    )
                    Texts.BodyMedium(
                        text = userName,
                        color = AppTheme.current.colors.onSurface
                    )
                }
                Card(
                    shape = RoundedCornerShape(200.dp),
                    colors = CardDefaults.cardColors(containerColor = statusBg)
                ) {
                    Texts.LabelSmall(
                        modifier = Modifier.padding(horizontal = p8, vertical = p2),
                        text = statusLabel,
                        color = statusColor
                    )
                }
            }

            Texts.BodyMedium(
                text = "$bagName x $bagsCount",
                color = AppTheme.current.colors.onSurface
            )
            Texts.TitleMedium(
                text = amountText,
                color = AppTheme.current.colors.onSurface
            )
        }
    }
}

@Composable
fun OverviewFilterPill(
        text: String,
        expanded: (Long?) -> Unit,
        locations: List<com.example.mystybag.sharedmodels.project.responseModels.merchant.MerchantLocationResponseModel>,
        selectedLocationId: Long?
    ) {
        var menuExpanded by remember { mutableStateOf(false) }

        Box {
            Row(
                modifier = Modifier
                    .clip(RoundedCornerShape(100.dp))
                    .background(AppTheme.current.colors.surfaceContainerLowest)
                    .padding(horizontal = p16, vertical = p10)
                    .clickable { menuExpanded = true },
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(p8)
            ) {
                Texts.BodyMedium(text = text, color = AppTheme.current.colors.onSurface)
                DashboardOverviewComboArrow()
            }
            DropdownMenu(
                expanded = menuExpanded,
                onDismissRequest = { menuExpanded = false },
                containerColor = AppTheme.current.colors.surface,
                shape = RoundedCornerShape(p20),
                offset = DpOffset(x = 0.dp, y = 4.dp),
                modifier = Modifier.heightIn(max = 320.dp)
            ) {
                DropdownMenuItem(
                    text = { Text(sharedString(SR.strings.all_locations)) },
                    onClick = { expanded(null); menuExpanded = false }
                )
                if (locations.isNotEmpty()) {
                    HorizontalDivider(thickness = 1.dp, color = AppTheme.current.colors.outline)
                }
                locations.forEachIndexed { index, loc ->
                    DropdownMenuItem(
                        text = { Text(loc.title ?: "") },
                        onClick = { expanded(loc.id); menuExpanded = false }
                    )
                    if (index < locations.lastIndex) {
                        HorizontalDivider(thickness = 1.dp, color = AppTheme.current.colors.outline)
                    }
                }
            }
        }
    }

    @Composable
    fun OverviewFilterPillPeriod(
        selectedPeriod: DashboardPeriod,
        onPeriodSelected: (DashboardPeriod) -> Unit
    ) {
        var menuExpanded by remember { mutableStateOf(false) }
        val periodText = when (selectedPeriod) {
            DashboardPeriod.TODAY -> sharedString(SR.strings.today)
            DashboardPeriod.LAST_WEEK -> sharedString(SR.strings.last_week)
            DashboardPeriod.LAST_MONTH -> sharedString(SR.strings.last_month)
            DashboardPeriod.PERIOD -> sharedString(SR.strings.select_period)
        }
        Box {
            Row(
                modifier = Modifier
                    .clip(RoundedCornerShape(100.dp))
                    .background(AppTheme.current.colors.surfaceContainerLowest)
                    .padding(horizontal = p16, vertical = p10)
                    .clickable { menuExpanded = true },
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(p8)
            ) {
                Texts.BodyMedium(text = periodText, color = AppTheme.current.colors.onSurface)
                DashboardOverviewComboArrow()
            }
            DropdownMenu(
                expanded = menuExpanded,
                onDismissRequest = { menuExpanded = false },
                containerColor = AppTheme.current.colors.surface,
                shape = RoundedCornerShape(p20),
                offset = DpOffset(x = 0.dp, y = 4.dp),
                modifier = Modifier.heightIn(max = 280.dp)
            ) {
                val periodOptions = listOf(
                    DashboardPeriod.TODAY to sharedString(SR.strings.today),
                    DashboardPeriod.LAST_WEEK to sharedString(SR.strings.last_week),
                    DashboardPeriod.LAST_MONTH to sharedString(SR.strings.last_month),
                    DashboardPeriod.PERIOD to sharedString(SR.strings.select_period)
                )
                periodOptions.forEachIndexed { index, (period, label) ->
                    DropdownMenuItem(
                        text = { Text(label) },
                        onClick = { onPeriodSelected(period); menuExpanded = false }
                    )
                    if (index < periodOptions.lastIndex) {
                        HorizontalDivider(thickness = 1.dp, color = AppTheme.current.colors.outline)
                    }
                }
            }
        }
    }

@Composable
private fun OverviewMetricCard(
    modifier: Modifier = Modifier,
    title: String,
    value: String,
    valueKind: OverviewMetricValueKind
) {
    val shape = RoundedCornerShape(p28)
    val colors = AppTheme.current.colors
    val typography = AppTheme.current.typography
    val titleBaseStyle = typography.bodyMedium.copy(
        fontSize = 16.sp,
        lineHeight = 24.sp,
        fontWeight = FontWeight.Bold,
        letterSpacing = 0.5.sp
    )
    val valueBaseStyle = when (valueKind) {
        OverviewMetricValueKind.REVENUE -> metricValueStyle(typography.displaySmall.fontWeight)
        OverviewMetricValueKind.DISPLAY_SMALL -> metricValueStyle(typography.titleLarge.fontWeight)
    }
    Card(
        modifier = modifier.border(1.dp, colors.outlineVariant, shape),
        shape = shape,
        colors = CardDefaults.cardColors(containerColor = colors.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(p20),
            verticalArrangement = Arrangement.Center
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .fillMaxHeight(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(p8)
            ) {
                // Не weight(1f):weight(1f) — тогава всеки autosize вижда само ~половината ширина и
                // намалява шрифта въпреки празно място; стойността има нужда от повече хоризонтал.
                AutoResizeSingleLineText(
                    text = title,
                    baseStyle = titleBaseStyle,
                    color = colors.onPrimaryContainer,
                    minFontSizeSp = OVERVIEW_METRIC_TITLE_MIN_SP,
                    modifier = Modifier.weight(13f),
                    textAlign = TextAlign.Start
                )
                AutoResizeSingleLineText(
                    text = value,
                    baseStyle = valueBaseStyle,
                    color = colors.onSurface,
                    minFontSizeSp = OVERVIEW_METRIC_VALUE_MIN_SP,
                    // Inset + fit slack: Web/Skiko vs measurer; "0.00 €" at max font used to clip €.
                    modifier = Modifier.weight(7f).padding(end = p12),
                    textAlign = TextAlign.End,
                    horizontalFitSlackDp = p12,
                )
            }
        }
    }
}

private fun metricValueStyle(weight: androidx.compose.ui.text.font.FontWeight?): TextStyle {
    return TextStyle(
        fontSize = 36.sp,
        lineHeight = 44.sp,
        fontWeight = weight
    )
}

@Composable
fun SmallMetricCard(
    modifier: Modifier = Modifier,
    title: String,
    value: String,
    topBorderColor: Color
) {
    val shape = RoundedCornerShape(p28)
    Card(
        modifier = modifier
            .border(1.dp, AppTheme.current.colors.outlineVariant, shape),
        shape = shape,
        colors = CardDefaults.cardColors(containerColor = AppTheme.current.colors.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(3.dp)
                    .background(topBorderColor, RoundedCornerShape(topStart = 28.dp, topEnd = 28.dp))
            )
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(p20)
            ) {
                Texts.LabelSmall(
                    text = title,
                    color = AppTheme.current.colors.onSurfaceVariant,
                    maxLines = 1
                )
                Texts.DisplaySmall(
                    text = value,
                    color = AppTheme.current.colors.onSurface,
                    maxLines = 1
                )
            }
        }
    }
}

@Composable
fun AvgRatingCard(rating: Double, reviewCount: Int) {
    val shape = RoundedCornerShape(p28)
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .border(1.dp, AppTheme.current.colors.outlineVariant, shape),
        shape = shape,
        colors = CardDefaults.cardColors(containerColor = AppTheme.current.colors.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(p20),
            verticalArrangement = Arrangement.spacedBy(p12)
        ) {
            Text(
                text = sharedString(SR.strings.avg_rating),
                color = AppTheme.current.colors.onPrimaryContainer,
                style = AppTheme.current.typography.bodyMedium.copy(fontWeight = FontWeight.Bold)
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(p10),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Texts.DisplaySmall(
                    text = com.example.mystybag.core.expected.format("%.1f", rating),
                    color = AppTheme.current.colors.onSurface
                )
                Row(
                    horizontalArrangement = Arrangement.spacedBy(p9),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Figma: star 32x30, filled #EFC200, unfilled #DDDFDE
                    val starWidth = 32.dp
                    val starHeight = 30.dp
                    val filledStarColor = Color(0xFFEFC200)
                    val unfilledStarColor = Color(0xFFDDDFDE)
                    repeat(5) { index ->
                        val fillFraction = (rating - index).coerceIn(0.0, 1.0)
                        FractionalStar(
                            modifier = Modifier.size(starWidth, starHeight),
                            fillFraction = fillFraction.toFloat(),
                            filledColor = filledStarColor,
                            unfilledColor = unfilledStarColor
                        )
                    }
                }
            }
            Texts.BodySmall(
                text = sharedString(SR.strings.reviews_count, reviewCount),
                color = AppTheme.current.colors.onSurfaceVariant
            )
        }
    }
}

@Composable
private fun FractionalStar(
    modifier: Modifier,
    fillFraction: Float,
    filledColor: Color,
    unfilledColor: Color
) {
    Canvas(modifier = modifier) {
        val clampedFraction = fillFraction.coerceIn(0f, 1f)
        val clipFraction = areaAdjustedClipFraction(clampedFraction)
        val starPath = createStarPath(size.width, size.height)
        drawPath(path = starPath, color = unfilledColor)
        clipRect(right = size.width * clipFraction) {
            drawPath(path = starPath, color = filledColor)
        }
    }
}

private fun areaAdjustedClipFraction(fillFraction: Float): Float {
    val targetAreaFraction = fillFraction.coerceIn(0f, 1f)
    val areaStops = floatArrayOf(
        0f, 0.047f, 0.093f, 0.166f, 0.242f, 0.316f, 0.406f, 0.5f,
        0.6f, 0.684f, 0.762f, 0.834f, 0.91f, 0.953f, 0.976f, 0.991f, 1f
    )
    val clipStops = floatArrayOf(
        0f, 0.2f, 0.25f, 0.3f, 0.35f, 0.4f, 0.45f, 0.5f,
        0.55f, 0.6f, 0.65f, 0.7f, 0.75f, 0.8f, 0.85f, 0.9f, 1f
    )
    for (i in 0 until areaStops.lastIndex) {
        val a0 = areaStops[i]
        val a1 = areaStops[i + 1]
        if (targetAreaFraction <= a1) {
            val t = ((targetAreaFraction - a0) / (a1 - a0)).coerceIn(0f, 1f)
            return clipStops[i] + (clipStops[i + 1] - clipStops[i]) * t
        }
    }
    return 1f
}

private fun createStarPath(width: Float, height: Float): Path {
    val path = Path()
    val cx = width / 2f
    val cy = height / 2f
    val outerRadius = min(width, height) / 2f
    val innerRadius = outerRadius * 0.5f
    val startAngle = -PI / 2.0

    repeat(10) { pointIndex ->
        val angle = startAngle + (PI / 5.0) * pointIndex
        val radius = if (pointIndex % 2 == 0) outerRadius else innerRadius
        val x = cx + cos(angle).toFloat() * radius
        val y = cy + sin(angle).toFloat() * radius
        if (pointIndex == 0) {
            path.moveTo(x, y)
        } else {
            path.lineTo(x, y)
        }
    }
    path.close()
    return path
}

@Composable
fun CategoryRatingCard(name: String, rating: Double) {
    val shape = RoundedCornerShape(p28)
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .border(1.dp, AppTheme.current.colors.outlineVariant, shape),
        shape = shape,
        colors = CardDefaults.cardColors(containerColor = AppTheme.current.colors.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(p20),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Texts.BodyMedium(
                text = name,
                color = AppTheme.current.colors.onSurface,
                maxLines = 2
            )
            Row(
                horizontalArrangement = Arrangement.spacedBy(p2),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Texts.BodyMedium(
                    text = com.example.mystybag.core.expected.format("%.1f", rating),
                    color = AppTheme.current.colors.onSurface
                )
                Image(
                    painter = painterResource(SRC.images.star_icon),
                    modifier = Modifier.size(16.dp),
                    colorFilter = ColorFilter.tint(Color(0xFFEFC200))
                )
            }
        }
    }
}

